import { useEffect, useMemo } from 'react'
import { format } from 'date-fns'
import type { INotes } from '../../../../redux/flows/portal/notes'
import { setHistorySelectedNoteId } from '../../../../redux/flows/portal/notes'
import { useAppDispatch, useAppSelector } from '../../../../redux/store'
import { convertUtcToEst } from '../../../../utils/DateAndTimeFormat'

export const useNotesHistory = () => {
  const dispatch = useAppDispatch()
  const currentPatientId = useAppSelector((state) => state.notesPortal.currentPatientId)
  const patientNotes = useAppSelector((state) => state.notesPortal.patientNotes)
  const historySelectedNoteId = useAppSelector((state) => state.notesPortal.historySelectedNoteId)

  // Extract notes for the current patient
  // Use currentPatientId (stored when selecting pending note) to get all patient notes
  // This ensures history always shows for the selected pending note's patient,
  // even when a history note is selected
  const currentPatientNotes = useMemo(() => {
    if (currentPatientId) {
      return patientNotes[currentPatientId]?.notes || []
    }
    return []
  }, [patientNotes, currentPatientId])

  // Helper to format UTC → EST
  const formatDate = (time: string, formatString: string) => {
    const stamp = new Date(time)
    const estTime = convertUtcToEst(stamp)
    return format(estTime, formatString)
  }

  const handleNoteClick = (noteId: string) => {
    dispatch(setHistorySelectedNoteId(noteId))
  }

  // Flatten & sort history (newest first)
  const flatHistory = useMemo(() => {
    if (!currentPatientNotes || currentPatientNotes.length === 0) return []

    const todayKey = formatDate(new Date().toISOString(), 'MM.dd.yyyy')

    return [...currentPatientNotes]
      .sort((a: INotes, b: INotes) => {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      })
      .map((note: INotes) => {
        const dateKey = formatDate(note.createdAt, 'MM.dd.yyyy')

        return {
          id: note.id,
          date: dateKey,
          time: formatDate(note.createdAt, 'h:mm a'),
          isToday: dateKey === todayKey,
          displayDate: dateKey === todayKey ? 'Today' : dateKey,
          note,
        }
      })
  }, [currentPatientNotes])

  // History note auto-selection is handled in Redux (getNotesAsync.fulfilled)
  // This ensures the first history item is selected by default when patient notes are loaded

  return {
    flatHistory,
    currentPatientNotes,
    hasHistory: currentPatientNotes.length > 0,
    handleNoteClick,
    historySelectedNoteId,
  }
}
